import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { EntityTablePage, DeleteConfirmationConfig } from "@/components/EntityTablePage";
import { NoPermissionScreen } from "@/components/screens/shared/NoPermissionScreen";
import { getInvoiceTableConfig } from "@/config/tableConfigs/invoiceTableConfig";
import { getBillingItemTableConfig } from "@/config/tableConfigs/billingItemTableConfig";
import { MarketOption } from "@/config/tableConfigs/projectTableConfig";
import { invoiceService } from "@/services/invoiceService";
import { InvoiceType } from "@/types/db/invoices";
import useAuth from "@/hooks/useAuth";
import { supabase } from "@/lib/supabase-client";
import useToast from "@/hooks/useToast";
import { billingItemService } from "@/services/billingItemService";
import { FromDB } from "@/types";
import useContextMenu from "@/hooks/useContextMenu";
import { Button } from "@/components/ui/Button";
import { useTheme } from "@/hooks/useTheme";
import { StyleSheet, View } from "react-native";
import { router } from "expo-router";
import { DeleteInvoiceModal } from "@/components/Modals/DeleteInvoiceModal";
import { DeleteBillingItemModal } from "@/components/Modals/DeleteBillingItemModal";
import { ExportInvoiceModal } from "@/components/Modals/ExportInvoiceModal";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { useSetPageTitle } from "@/hooks/useSetPageTitle";
import { useModal } from "@/hooks/useModal";
import { convertToCSV, downloadCSV } from "@/lib/csvUtils";

export default function InvoicesPage() {
  const { colors } = useTheme();
  const { userRoles, displayNames } = useAuth();
  const toast = useToast();
  const contextMenu = useContextMenu();
  const { openModal, closeModal } = useModal();
  const [data, setData] = useState<InvoiceType[]>([]);
  const [loading, setLoading] = useState(true);
  const [route, setRoute] = useState("Billing Items");
  const [marketOptions, setMarketOptions] = useState<MarketOption[]>([]);
  const isBillingItems = route === "Billing Items";
  useSetPageTitle(displayNames?.invoices?.plural);

  // Cache filter/sort/groupby state per view so toggling preserves filters
  const queryCache = useRef<Record<string, any>>({});
  const handleQueryChange = useCallback(
    (query: any) => {
      queryCache.current[route] = query;
    },
    [route],
  );

  // Fetch markets on mount
  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .schema("lists")
        .from("markets")
        .select("id, name")
        .order("name", { ascending: true });

      if (data) {
        setMarketOptions(data.map((m) => ({ value: m.id, label: m.name })));
      }
    })();
  }, []);

  const tableConfig = useMemo(
    () =>
      isBillingItems
        ? getBillingItemTableConfig(userRoles, displayNames)
        : getInvoiceTableConfig(userRoles, marketOptions, displayNames),
    [isBillingItems, userRoles, marketOptions, displayNames],
  );

  const fetchData = async () => {
    setLoading(true);
    try {
      const result = isBillingItems ? await billingItemService.getBillingItems() : await invoiceService.getInvoices();
      setData(result);
    } catch (error) {
      if (error) throw new Error(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route]);

  const handleDuplicate = async (row: any) => {
    try {
      const duplicated = await billingItemService.duplicateBillingItem(row.id);
      toast.showToast({
        title: "Billing item duplicated",
        toastType: "success",
      });
      fetchData(); // Refresh the table
    } catch (error: any) {
      toast.showToast({
        title: "Failed to duplicate",
        message: error.message,
        toastType: "error",
      });
    }
  };

  const menuContent = (row: any) => {
    return (
      <View>
        <Button
          text="Duplicate"
          iconSize={18}
          icon="content-duplicate"
          style={styles.menuItems}
          onPress={() => {
            contextMenu.setVisible(false);
            handleDuplicate(row);
          }}
        />
        {/* <Button */}
        {/*   text="View" */}
        {/*   iconSize={18} */}
        {/*   icon="exit-to-app" */}
        {/*   style={styles.menuItems} */}
        {/*   onPress={() => { */}
        {/*     router.setParams({ panel: "invoice", id: row.id }); */}
        {/*     router.setParams({ new: undefined }); */}
        {/*     contextMenu.setVisible(false); */}
        {/*   }} */}
        {/* /> */}
      </View>
    );
  };

  const buildProjectId = (inv: any): string => {
    const projectId = inv.project_id || inv.project?.id || "";
    const created = inv.created_at ? new Date(inv.created_at) : new Date();
    const yy = String(created.getFullYear()).slice(-2);
    const shortUuid = projectId.replace(/-/g, "").slice(0, 8).toUpperCase();
    return `${yy}QS${shortUuid}`;
  };

  const formatSageDate = (isoString: string | null): string => {
    if (!isoString) return "";
    const d = new Date(isoString);
    return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
  };

  const runSageExport = useCallback(
    async ({ selectedIds }: any) => {
      const freshData = await invoiceService.getInvoices();
      const selectedSet = new Set(selectedIds);
      const selected = freshData.filter((inv: any) => selectedSet.has(inv.id));

      const rows: any[] = [];
      selected.forEach((inv: any) => {
        const lineItems = inv.line_items || [];
        const invoiceDate = formatSageDate(inv.invoice_date);
        const customerId = inv.client?.meta_data?.sage_id || "";
        const projectId = buildProjectId(inv);
        const docNo = inv.invoice_number || "";

        lineItems.forEach((item: any) => {
          rows.push({
            DONOTIMPORT: "",
            TRANSACTIONTYPE: "QSR Sales invoice",
            DATE: invoiceDate,
            GLPOSTINGDATE: invoiceDate,
            DOCUMENTNO: docNo,
            CUSTOMER_ID: customerId,
            DATEDUE: "",
            CURRENCY: "USD",
            BASECURR: "USD",
            EXCHRATEDATE: "",
            STATE: "Pending",
            LINE: "1",
            BILLTO: customerId ? `${customerId}Billing` : "",
            SHIPTO: customerId ? `${customerId}Billing` : "",
            ITEMID: inv.market_sage_name || "",
            QUANTITY: "1",
            UNIT: "Each",
            PRICE: item.unit_price ?? "",
            LOCATIONID: "3017",
            DEPARTMENTID: "QSR",
            SODOCUMENTENTRY_PROJECTID: projectId,
            PROJECTID: projectId,
            SODOCUMENTENTRY_CUSTOMERID: customerId,
            TAXSOLUTIONID: "None",
          });
        });
      });

      const columns = [
        "DONOTIMPORT",
        "TRANSACTIONTYPE",
        "DATE",
        "GLPOSTINGDATE",
        "DOCUMENTNO",
        "CUSTOMER_ID",
        "DATEDUE",
        "CURRENCY",
        "BASECURR",
        "EXCHRATEDATE",
        "STATE",
        "LINE",
        "BILLTO",
        "SHIPTO",
        "ITEMID",
        "QUANTITY",
        "UNIT",
        "PRICE",
        "LOCATIONID",
        "DEPARTMENTID",
        "SODOCUMENTENTRY_PROJECTID",
        "PROJECTID",
        "SODOCUMENTENTRY_CUSTOMERID",
        "TAXSOLUTIONID",
      ];

      const csv = convertToCSV(rows, columns);
      downloadCSV(csv, "sage invoices export.csv");

      toast.showToast({
        title: `Exported ${selected.length} invoice${selected.length !== 1 ? "s" : ""} (${rows.length} line${rows.length !== 1 ? "s" : ""})`,
        toastType: "success",
      });
    },
    [toast],
  );

  const handleSageExport = useCallback(
    async ({ selectedIds, selectedMap }: any) => {
      const skipConfirmation = await AsyncStorage.getItem("invoice_sage_export_skip_confirmation");

      if (skipConfirmation === "true") {
        await runSageExport({ selectedIds });
        return;
      }

      openModal(
        <ExportInvoiceModal
          count={selectedIds.length}
          onConfirm={async (dontShowAgain) => {
            closeModal();
            if (dontShowAgain) {
              await AsyncStorage.setItem("invoice_sage_export_skip_confirmation", "true");
            }
            await runSageExport({ selectedIds });
          }}
          onCancel={closeModal}
        />,
      );
    },
    [openModal, closeModal, runSageExport],
  );

  // Check if user has invoices read permission
  const hasInvoicesAccess = useMemo(() => {
    return userRoles?.some((r) => r.permissions?.invoices?.read === true) ?? false;
  }, [userRoles]);

  const deleteConfirmation: DeleteConfirmationConfig<FromDB<InvoiceType>> = useMemo(
    () => ({
      storageKey: isBillingItems ? "billing_item_delete_skip_confirmation" : "invoice_delete_skip_confirmation",
      renderModal: ({ count, onConfirm, onCancel }) =>
        isBillingItems ? (
          <DeleteBillingItemModal
            count={count}
            onConfirm={async (dontShowAgain) => {
              if (dontShowAgain) {
                await AsyncStorage.setItem("billing_item_delete_skip_confirmation", "true");
              }
              onConfirm();
            }}
            onCancel={onCancel}
          />
        ) : (
          <DeleteInvoiceModal
            count={count}
            onConfirm={async (dontShowAgain) => {
              if (dontShowAgain) {
                await AsyncStorage.setItem("invoice_delete_skip_confirmation", "true");
              }
              onConfirm();
            }}
            onCancel={onCancel}
          />
        ),
    }),
    [isBillingItems],
  );

  if (!hasInvoicesAccess) {
    return <NoPermissionScreen entityName={displayNames?.invoices?.plural || "Invoices"} />;
  }

  return (
    <EntityTablePage<FromDB<InvoiceType>>
      {...tableConfig}
      initialQuery={queryCache.current[route]}
      onActiveQueryChange={handleQueryChange}
      // fetchData={invoiceService.getInvoices}
      updateEntities={isBillingItems ? billingItemService.saveRowPatches : invoiceService.saveRowPatches}
      enableSave={isBillingItems}
      deleteEntities={isBillingItems ? billingItemService.deleteBillingItemsByIds : invoiceService.deleteInvoicesByIds}
      deleteConfirmation={deleteConfirmation}
      rowNavIcon={isBillingItems ? "dots-vertical" : undefined}
      enableCreate={isBillingItems}
      onAction={
        isBillingItems
          ? (row, ref) => {
              if (!ref) return;
              ref.current?.measureInWindow((x, y, _w, h) => {
                contextMenu.setLocation({ x, y: y + (h ?? 0) });
              });
              contextMenu.setMenuContent(menuContent(row));
              contextMenu.setStyle({ backgroundColor: colors.background, overflow: "hidden" });
              contextMenu.setVisible(true);
            }
          : undefined
      }
      useCustomData={() => ({ data: data, loading, refresh: fetchData })}
      selectionActions={
        isBillingItems
          ? () => [
              {
                id: "create-invoice",
                label: "Create Invoice",
                icon: "file-plus-outline",
                onPress: () => {
                  router.setParams({
                    panel: "invoice",
                    new: "true",
                  });
                },
              },
            ]
          : ({ selectedIds, selectedMap }) => [
              {
                id: "export-sage",
                label: "Export to Sage",
                icon: "export-variant",
                onPress: () => handleSageExport({ selectedIds, selectedMap }),
              },
            ]
      }
      toggleProps={{
        choices: ["Billing Items", "Invoices"],
        onPick: (item) => setRoute(item as string),
      }}
    />
  );
}
const styles = StyleSheet.create({
  menuItems: {
    backgroundColor: "transparent",
    borderWidth: 0,
    justifyContent: "flex-start",
    gap: 8,
  },
});
